import React, { useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import { Box, Button, Theme, Typography } from "@material-ui/core";
import Backdrop from "@material-ui/core/Backdrop";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Snackbar from "@material-ui/core/Snackbar";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import CloseIcon from "@material-ui/icons/Close";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import Alert from "@material-ui/lab/Alert";
import { CSSProperties } from "@material-ui/styles";
import clsx from "clsx";
import { useFormik } from "formik";
import { decode } from "js-base64";
import { parse } from "query-string";
import { object, SchemaOf, string } from "yup";
import { setPasswordAction } from "src/app-redux/auth/actions/authActions";
import { selectAuthError, selectStatus } from "src/app-redux/auth/authSlice";
import { useAppDispatch, useAppSelector } from "src/app-redux/hooks";
import NJSLogo from "src/assets/images/tfg_mainlogo.svg";
import { AuthRoute } from "../auth.routes";
import CommonLoader from "src/components/Loader/CommonLoader";

const mediumText: CSSProperties = {
  fontWeight: 500,
} as const;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    footNote: {
      display: "block",
      maxWidth: "320px",
      margin: "auto",
      fontSize: "12px",
      color: "#96A4B9",
    },
    forgotPasswordButton: {
      paddingTop: "0",
      fontSize: "12px",
      color: "#488CFB",
      textTransform: "capitalize",
      "&:hover": {
        backgroundColor: "transparent",
        textDecoration: "underline",
      },
    },
    backdrop: {
      zIndex: theme.zIndex.modal + 1,
      color: "#fff",
    },
    headerLogo: {
      textAlign: "center",
    },
    headerLogoLink: {
      textAlign: "center",
      width: "100%",
      float: "left",
    },
  })
);

interface IForm {
  repeatPassword: string;
  password: string;
}
const validationSchema: SchemaOf<IForm> = object({
  password: string()
    .min(8, "Password should be of minimum 8 characters length")
    .max(256, "Password should not exceed more than 256 characters")
    .required("Password is required"),
  repeatPassword: string()
    .min(8, "Password should be of minimum 8 characters length")
    .max(256, "Password should not exceed more than 256 characters")
    .required("Password is required")
    .test("passwords-match", "Passwords must match", function (value) {
      return this.parent.password === value;
    }),
});
export function SetPasswordPage() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const authStatus = useAppSelector(selectStatus);
  const authError = useAppSelector(selectAuthError);

  const [values, setValues] = useState({
    showPassword: false,
    showRepeatPassword: false,
  });

  const [alert1, setAlert] = useState(Boolean(authStatus === "failed" && authError));
  useEffect(() => {
    if (authStatus === "failed" && authError) setAlert(true);
    if (!authError) setAlert(false);
  }, [authStatus, authError]);
  const handleClose = (_event?: React.SyntheticEvent, reason?: string) => {
    if (reason === "clickaway") return;
    setAlert(false);
  };

  const handleClickShowPassword = () => {
    setValues({ ...values, showPassword: !values.showPassword });
  };
  const handleClickShowRepeatPassword = () => {
    setValues({ ...values, showRepeatPassword: !values.showRepeatPassword });
  };
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const formik = useFormik<IForm>({
    initialValues: {
      repeatPassword: "",
      password: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const { email } = parse(location.search);
      const decodedEmail = decode(email ? (email as string) : "");

      await dispatch(setPasswordAction({ Email: decodedEmail, Password: values.password }));
    },
  });
  return authStatus === "PasswordResetSuccessfull" ? (
    <Redirect to={{ pathname: AuthRoute.path }} />
  ) : (
    <>
      <Backdrop className={classes.backdrop} open={Boolean(authStatus === "loading")}>
        <CommonLoader />
      </Backdrop>
      <Snackbar open={alert1} autoHideDuration={4000} onClose={handleClose}>
        <Alert variant="filled" severity="error">
          Invalid credentials. Please try again.
          <IconButton size="small" aria-label="close" color="inherit" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Alert>
      </Snackbar>
      Î
      <div className="login-page">
        <div className="left-section">
          <div className="inner">
            <Box my={2} className="headerLogo" textAlign="Center">
              <a href="/" className="headerLogoLink">
                <img src={NJSLogo} alt="logo" width="200" />
              </a>
            </Box>
            <div className="form-wrapper">
              <h2 className="form-heading">Set Password</h2>
              <form onSubmit={formik.handleSubmit}>
                <TextField
                  size="small"
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  label="Password"
                  name="password"
                  type={values.showPassword ? "text" : "password"}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {values.showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box m={4} />
                <TextField
                  size="small"
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  label="Repeat Password"
                  name="repeatPassword"
                  type={values.showRepeatPassword ? "text" : "password"}
                  value={formik.values.repeatPassword}
                  onChange={formik.handleChange}
                  error={formik.touched.repeatPassword && Boolean(formik.errors.repeatPassword)}
                  helperText={formik.touched.repeatPassword && formik.errors.repeatPassword}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowRepeatPassword}
                          edge="end"
                        >
                          {values.showRepeatPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box m={4} />

                <Button type="submit" variant="contained" color="secondary" fullWidth>
                  Set Password
                </Button>
              </form>
            </div>
            <Typography
              component="span"
              variant="body1"
              color="textPrimary"
              style={mediumText}
              className={clsx(classes.footNote)}
            >
              Copyright©, NLC, {new Date().getFullYear()}
            </Typography>
          </div>
        </div>
        <div className="right-section">
          <div className="login-bg-pattern"></div>
        </div>
      </div>
    </>
  );
}
